import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { WordBankFilter } from "../word-bank.service";

type SearchItemConfig = {
  type: "input";
  placeholder: string;
};

type Props = {
  updateQuery: (filter: WordBankFilter) => void;
  initialQuery?: WordBankFilter;
};

const searchItems: Record<keyof WordBankFilter, SearchItemConfig> = {
  id: {
    type: "input",
    placeholder: "Enter word bank document ID",
  },
  userId: {
    type: "input",
    placeholder: "Enter user ID",
  },
};

type ActiveBadge = {
  key: keyof WordBankFilter;
  label: string;
  value: string;
};

export const WordBankSearch = ({ updateQuery, initialQuery = {} }: Props) => {
  const items = Object.keys(searchItems) as (keyof WordBankFilter)[];
  const [selected, setSelected] = useState<keyof WordBankFilter>(items[0]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<WordBankFilter>(initialQuery);
  const [activeBadges, setActiveBadges] = useState<ActiveBadge[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const previousSelectedRef = useRef<keyof WordBankFilter | null>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  const updateBadges = useCallback((filters: WordBankFilter) => {
    const badges: ActiveBadge[] = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;
      badges.push({
        key: key as keyof WordBankFilter,
        label: key === "userId" ? "User ID" : "ID",
        value,
      });
    });

    setActiveBadges(badges);
  }, []);

  const handleSearch = useCallback(
    (key: keyof WordBankFilter, value: string) => {
      const newFilters: WordBankFilter = { ...currentFilters };

      if (value) newFilters[key] = value;
      else delete newFilters[key];

      setCurrentFilters(newFilters);
      updateQuery(newFilters);
      updateBadges(newFilters);
    },
    [currentFilters, updateBadges, updateQuery]
  );

  const debounceSearch = useCallback(
    (key: keyof WordBankFilter, value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handleSearch(key, value), 300);
    },
    [handleSearch]
  );

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);

    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    setCurrentFilters(initialQuery);
    updateBadges(initialQuery);
  }, [initialQuery, updateBadges]);

  useEffect(() => {
    if (previousSelectedRef.current !== null && previousSelectedRef.current !== selected) {
      setQuery("");
    }
    previousSelectedRef.current = selected;
  }, [selected]);

  const currentConfig = searchItems[selected];

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto">
        <div ref={ref} className="relative group">
          <label htmlFor="word-bank-search-dropdown" className="sr-only">
            Search category
          </label>

          <div
            className={`
              flex rounded-2xl overflow-hidden transition-all duration-300 ease-out
              bg-white/95 backdrop-blur-sm border-2 shadow-xl
              ${
                focused || open
                  ? "border-emerald-400 shadow-2xl shadow-emerald-200/60 scale-[1.02] ring-4 ring-emerald-100/50"
                  : "border-slate-200 hover:border-emerald-300 hover:shadow-2xl hover:scale-[1.01]"
              }
            `}
          >
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              className={`
                flex items-center px-6 py-4 bg-gradient-to-r transition-all duration-200
                ${
                  open
                    ? "from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-lg"
                    : "from-slate-100 via-slate-50 to-emerald-50 text-slate-700 hover:from-emerald-100 hover:via-teal-100 hover:to-cyan-100 hover:text-emerald-800"
                }
                border-r border-slate-200 font-medium
              `}
            >
              <span className="truncate">{selected === "userId" ? "User ID" : "ID"}</span>
              <FaChevronDown className={`ml-3 w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>

            <div className="flex-1 relative">
              <input
                id="word-bank-search-dropdown"
                type="search"
                value={query}
                onChange={(event) => {
                  const value = event.target.value.trim();
                  setQuery(event.target.value);
                  debounceSearch(selected, value);
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={currentConfig.placeholder}
                className="w-full px-6 py-4 text-slate-800 placeholder-slate-500 bg-transparent focus:border-none focus:outline-none focus:ring-0 text-lg flex-grow border-none focus:text-emerald-700 transition-colors duration-200"
              />
              {focused && (
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/40 via-teal-50/40 to-cyan-50/40 pointer-events-none border-none" />
              )}
            </div>

            {query.trim() && (
              <div className="px-4 py-4 flex items-center justify-center text-emerald-500">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>

          <div
            className={`
              absolute top-full left-0 mt-2 z-50 transition-all duration-300 ease-out origin-top w-auto
              bg-white/98 backdrop-blur-sm border border-emerald-200 rounded-2xl shadow-2xl overflow-hidden
              ${open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}
            `}
          >
            <ul className="pl-0 list-none m-0 text-sm">
              {items.map((item, index) => (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setOpen(false);
                    }}
                    className={`
                      w-full px-20 py-3 text-left transition-all duration-150 flex items-center block
                      ${
                        selected === item
                          ? "bg-gradient-to-r from-emerald-100 via-teal-100 to-cyan-100 text-emerald-800 font-medium border-r-4 border-emerald-400 shadow-sm"
                          : "text-slate-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:via-teal-50 hover:to-cyan-50 hover:text-emerald-700"
                      }
                    `}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span className="flex-1">{item === "userId" ? "User ID" : "ID"}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {activeBadges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeBadges.map((badge) => (
              <button
                key={`${badge.key}-${badge.value}`}
                onClick={() => handleSearch(badge.key, "")}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border bg-cyan-50 text-cyan-700 border-cyan-200"
                title={`Remove ${badge.label}`}
              >
                <span className="font-medium">{badge.label}:</span>
                <span>{badge.value}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WordBankSearch;
